// WalletController handles wallet-related operations such as importing, creating, and managing assets.

import { useWallet } from "@stores/wallet.store";
import {
    IAsset,
    IAssetDetail,
    IAssetBalance,
    ISwapRequest,
    IApiResponse,
    ITransactionResponse,
    IUserWallet,
    ISwapExternalMessagesResponsePayload,
    ISwapEmulationRequest,
    ISwapEmulationResponse,
    ITransferRequest,
    TransferMessageResponse,
    IGetPoolsResponse,
    ICreateSniperPayload,
    ICreateSniperResponse,
    ISnipeItem,
    ICancelSniperPayload,
} from "./IWalletController";

// --- Mempad Token Info Types ---
export interface MempadTokenInfo {
    totalSupply: string;
    priceInTon: string;
    marketCapInTon: string;
}
export interface MempadTokenApiResponse {
    data: MempadTokenInfo;
    status: string;
    message: string;
}

const apiUrl = import.meta.env.VITE_BASE_URL;

export const WalletController = {
    /**
     * Imports a wallet using mnemonics.
     * @param {string} mnemonics - The mnemonics to import the wallet.
     * @returns {Promise<any>} - The response from the server.
     */
    async import(mnemonics: string) {
        const token = useWallet.getState().jwt;
        const response = await fetch(`${apiUrl}/wallet/import`, {
            method: "POST",
            headers: {
                "Content-type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                contract: "v5r1_final",
                mnemonics,
            }),
        });
        return response.json();
    },
    /**
     * Reject a unconfirmed wallet using a GUID.
     * @param {string} guid - The GUID of the wallet to reject.
     * @returns {Promise<any>} - The response from the server.
     */
    async reject(guid: string) {
        const token = useWallet.getState().jwt;
        const response = await fetch(`${apiUrl}/wallet/reject`, {
            method: "POST",
            headers: {
                "Content-type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ guid }),
        });
        return response.json();
    },

    /**
     * Confirms a wallet import using a GUID.
     * @param {string} guid - The GUID of the wallet to confirm.
     * @returns {Promise<any>} - The response from the server.
     */
    async confirm(guid: string) {
        const token = useWallet.getState().jwt;
        const response = await fetch(`${apiUrl}/wallet/confirm`, {
            method: "POST",
            headers: {
                "Content-type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ guid }),
        });
        return response.json();
    },

    /**
     * Creates a new wallet.
     * @returns {Promise<any>} - The response from the server.
     */
    async create() {
        const token = useWallet.getState().jwt;
        const response = await fetch(`${apiUrl}/wallet/generate`, {
            method: "POST",
            headers: {
                "Content-type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                contract: "v5r1_final",
            }),
        });
        return response.json();
    },

    /**
     * Finds assets based on provided asset details.
     * @param {IAssetDetail[]} assets - An array of asset details to find.
     * @returns {Promise<IAsset[]>} - An array of found assets.
     */
    // --- 429 global cooldown vars ---
    _findAssetsCooldownUntil: 0,
    async findAssets(assets: IAssetDetail[]): Promise<IAsset[]> {
        this._findAssetsCooldownUntil += 550;

        if (this._findAssetsCooldownUntil > 550) {
            await new Promise((r) => setTimeout(r, this._findAssetsCooldownUntil - 550));
        }

        const token = useWallet.getState().jwt;
        const response = await fetch(`${apiUrl}/asset/find`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(assets),
        });
        if (response.status === 429) {
            // Set 10s cooldown
            WalletController._findAssetsCooldownUntil = Date.now() + 10000;
            throw new Error("429: Too Many Requests. Please wait 11s before retrying. ");
        }
        const { data } = await response.json();
        return data;
    },

    /**
     * Gets balances for specified assets in a wallet.
     * @param {string} walletGuid - The GUID of the wallet.
     * @param {IAsset[]} assets - An array of assets to check balances for.
     * @returns {Promise<IAssetBalance[]>} - An array of asset balances.
     */
    async getBalances(walletGuid: string, assets: IAsset[]): Promise<IApiResponse<IAssetBalance[]>> {
        const token = useWallet.getState().jwt;

        const response = await fetch(`${apiUrl}/wallet/balances`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify([
                {
                    walletGuid,
                    assetGuids: assets,
                },
            ]),
        });

        return await response.json();
    },

    /**
     * Retrieves all wallets for the user.
     * @returns {Promise<IApiResponse<IUserWallet[]>>} - An array of user wallets.
     */
    _getWalletsCooldownUntil: 0,
    async getWallets(): Promise<IApiResponse<IUserWallet[]>> {
        const COOLDOWN = 1000;

        this._getWalletsCooldownUntil += COOLDOWN;

        if (this._getWalletsCooldownUntil > COOLDOWN) {
            await new Promise((r) => setTimeout(r, this._getWalletsCooldownUntil - COOLDOWN));
        }

        const token = useWallet.getState().jwt;
        const response = await fetch(`${apiUrl}/wallet`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.json();
    },

    /**
     * Get best pools for a given asset pair ON EACH DEX
     * @param {IGetPoolsRequest} params - params.
     * @returns {Promise<IApiResponse<IGetPoolsResponse | null>>} return - Best available pools.
     */
    _getPoolsCooldownUntil: 0,
    async getPools({ assetInGuid, assetOutGuid }: ISwapRequest): Promise<IApiResponse<IGetPoolsResponse | null>> {
        const COOLDOWN = 1000;

        this._getPoolsCooldownUntil += COOLDOWN;

        if (this._getPoolsCooldownUntil > COOLDOWN) {
            await new Promise((r) => setTimeout(r, this._getPoolsCooldownUntil - COOLDOWN));
        }

        const token = useWallet.getState().jwt;

        const response = await fetch(`${apiUrl}/dex/pools`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                assetInGuid,
                assetOutGuid,
            }),
        });
        return response.json();
    },

    //#region SWAP
    /**
     * Executes an internal swap between assets.
     * @param {ISwapRequest} params - The swap request parameters.
     * @returns {Promise<IApiResponse<ITransactionResponse | null>>} - The response from the swap operation.
     */
    async swapInternal({
        walletGuid,
        amount,
        assetInGuid,
        assetOutGuid,
        poolAddress,
        slippagePercentage,
    }: ISwapRequest): Promise<IApiResponse<ITransactionResponse | null>> {
        const token = useWallet.getState().jwt;

        const response = await fetch(`${apiUrl}/dex/swap/internal`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                walletGuid,
                amount,
                assetInGuid,
                assetOutGuid,
                poolAddress,
                slippagePercentage,
            }),
        });
        return response.json();
    },

    async memeSwapInternal({
        walletGuid,
        amount,
        assetInGuid,
        assetOutGuid,
        mempadType,
        snipeId,
    }: ISwapRequest): Promise<IApiResponse<ITransactionResponse | null>> {
        const token = useWallet.getState().jwt;

        const response = await fetch(`${apiUrl}/mempad/swap/internal`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                walletGuid,
                amount,
                assetInGuid,
                assetOutGuid,
                mempadType,
                snipeId,
            }),
        });
        return response.json();
    },

    /**
     * Executes an external swap between assets.
     * @param {ISwapRequest} params - The swap request parameters.
     * @returns {Promise<IApiResponse<ISwapExternalMessagesResponsePayload[] | null>>} - The response from the swap operation.
     */
    async swapExternal({
        walletGuid,
        amount,
        assetInGuid,
        assetOutGuid,
        poolAddress,
        slippagePercentage,
    }: ISwapRequest): Promise<IApiResponse<ISwapExternalMessagesResponsePayload[] | null>> {
        const token = useWallet.getState().jwt;

        const response = await fetch(`${apiUrl}/dex/swap/external`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                walletGuid,
                amount,
                assetInGuid,
                assetOutGuid,
                poolAddress,
                slippagePercentage,
            }),
        });
        return response.json();
    },

    async memeSwapExternal({
        walletGuid,
        amount,
        assetInGuid,
        assetOutGuid,
        mempadType,
        snipeId,
    }: ISwapRequest): Promise<IApiResponse<ISwapExternalMessagesResponsePayload[] | null>> {
        const token = useWallet.getState().jwt;

        const response = await fetch(`${apiUrl}/mempad/swap/external`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                walletGuid,
                amount,
                assetInGuid,
                assetOutGuid,
                mempadType,
                snipeId,
            }),
        });
        return response.json();
    },

    /**
     * Emulates a swap operation.
     * @param {ISwapEmulationRequest[]} data - The swap emulation request data.
     * @returns {Promise<IApiResponse<ISwapEmulationResponse[] | null>>} - The response from the emulation.
     */
    async swapEmulate(data: ISwapEmulationRequest[]): Promise<IApiResponse<ISwapEmulationResponse[] | null>> {
        const token = useWallet.getState().jwt;

        const response = await fetch(`${apiUrl}/dex/swap/emulate`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        });

        if (response.status == 429) {
            await new Promise((r) => setTimeout(r, 500));
            return this.swapEmulate(data);
        }

        return response.json();
    },
    //#endregion

    //#region TRANSFER
    /**
     * Executes an internal transfer of assets.
     * @param {ITransferRequest} data - The transfer request data.
     * @returns {Promise<IApiResponse<ITransactionResponse | null>>} - The response from the transfer operation.
     */
    async trasferInternal(data: ITransferRequest): Promise<IApiResponse<ITransactionResponse | null>> {
        const token = useWallet.getState().jwt;

        const response = await fetch(`${apiUrl}/wallet/transfer/internal`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        });
        return response.json();
    },

    /**
     * Executes an external transfer of assets.
     * @param {ITransferRequest} data - The transfer request data.
     * @returns {Promise<IApiResponse<TransferMessageResponse | null>>} - The response from the transfer operation.
     */
    async trasferExternal(data: ITransferRequest): Promise<IApiResponse<TransferMessageResponse | null>> {
        const token = useWallet.getState().jwt;

        const response = await fetch(`${apiUrl}/wallet/transfer/external`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        });
        return response.json();
    },

    async createSniper(payload: ICreateSniperPayload): Promise<IApiResponse<ICreateSniperResponse>> {
        const token = useWallet.getState().jwt;

        if (!token) {
            throw new Error("Authentication token is missing.");
        }

        const response = await fetch(`${apiUrl}/sniper/create`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: "Failed to parse error response" }));
            console.error("Sniper creation failed:", response.status, errorData);
            throw new Error(`Failed to create sniper: ${errorData?.message || response.statusText}`);
        }

        return await response.json();
    },

    async createMemePadSniper(payload: ICreateSniperPayload): Promise<IApiResponse<ICreateSniperResponse>> {
        const token = useWallet.getState().jwt;

        if (!token) {
            throw new Error("Authentication token is missing.");
        }

        const response = await fetch(`${apiUrl}/sniper/mempad/create`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: "Failed to parse error response" }));
            console.error("Sniper creation failed:", response.status, errorData);
            throw new Error(`Failed to create sniper: ${errorData?.message || response.statusText}`);
        }

        return await response.json();
    },

    /**
     * Fetches the list of snipes for the user.
     * @returns {Promise<IApiResponse<ISnipeItem[]>>} - The list of snipes.
     */
    async getSniperList(): Promise<IApiResponse<ISnipeItem[]>> {
        const token = useWallet.getState().jwt;

        if (!token) {
            throw new Error("Authentication token is missing.");
        }

        const response = await fetch(`${apiUrl}/sniper/list`, {
            method: "GET", // Use GET for fetching lists
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json", // Optional for GET but good practice
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: "Failed to parse error response" }));
            console.error("Fetching sniper list failed:", response.status, errorData);
            throw new Error(`Failed to fetch sniper list: ${errorData?.message || response.statusText}`);
        }

        return await response.json();
    },

    /**
     * Cancels an existing snipe.
     * @param {ICancelSniperPayload} payload - The payload containing the snipeId (GUID) to cancel.
     * @returns {Promise<IApiResponse<Record<string, never>>>} - API response indicating success or failure.
     */
    async cancelSniper(payload: ICancelSniperPayload): Promise<IApiResponse<Record<string, never>>> {
        const token = useWallet.getState().jwt;

        if (!token) {
            throw new Error("Authentication token is missing.");
        }

        const response = await fetch(`${apiUrl}/sniper/cancel`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: "Failed to parse error response" }));
            console.error("Canceling sniper failed:", response.status, errorData);
            throw new Error(`Failed to cancel sniper: ${errorData?.message || response.statusText}`);
        }

        return await response.json();
    },

    // Function to update (find) sniper, similar to cancelSniper
    /**
     * Update snipe by sending an array of snipe IDs.
     * @param snipeIds - Array of snipe IDs to update
     */
    async updateSnipe(snipeIds: string[]): Promise<IApiResponse<any>> {
        const token = useWallet.getState().jwt;

        if (!token) {
            throw new Error("Authentication token is missing.");
        }

        const response = await fetch(`${apiUrl}/sniper/find`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(snipeIds),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: "Failed to parse error response" }));
            console.error("Finding sniper failed:", response.status, errorData);
            throw new Error(`Failed to update/find sniper: ${errorData?.message || response.statusText}`);
        }

        return await response.json();
    },

    async getSniperHistory(payload: { walletGuid: string }): Promise<IApiResponse<any>> {
        const token = useWallet.getState().jwt;
        if (!token) {
            throw new Error("Authentication token is missing.");
        }
        const response = await fetch(`${apiUrl}/sniper/history/list`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: "Failed to parse error response" }));
            console.error("Fetching sniper history failed:", response.status, errorData);
            throw new Error(`Failed to fetch sniper history: ${errorData?.message || response.statusText}`);
        }
        return await response.json();
    },

    async getReferralsList(): Promise<IApiResponse<any>> {
        const token = useWallet.getState().jwt;
        if (!token) {
            throw new Error("Authentication token is missing.");
        }
        const response = await fetch(`${apiUrl}/referral/list`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });
        return response.json();
    },

    async getReferralConditions(): Promise<IApiResponse<any>> {
        const token = useWallet.getState().jwt;
        if (!token) {
            throw new Error("Authentication token is missing.");
        }
        const response = await fetch(`${apiUrl}/referral/conditions`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });
        return response.json();
    },

    async updateDeployToken(guid: string): Promise<IApiResponse<any>> {
        const response = await fetch(`${apiUrl}/mempad/token/update`, {
            method: "POST",
            body: JSON.stringify({ tokenGuid: guid }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        return response.json();
    },
    /**
     * Fetch mempad token info (totalSupply, priceInTon, marketCapInTon) for a given token address.
     * @param {string} assetGuid - The token asset GUID to query.
     * @returns {Promise<MempadTokenApiResponse>} - The response from the server.
     */
    async getMempadTokenInfo(assetGuid: string): Promise<MempadTokenApiResponse> {
        const token = useWallet.getState().jwt;
        const response = await fetch(`${apiUrl}/mempad/mempad/token`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ assetGuid }),
        });
        return response.json();
    },

    /**
     * Fetch meme swap list for a given wallet GUID.
     * @param {string} walletGuid - The wallet GUID to query.
     * @returns {Promise<any>} - The response from the server.
     */
    async getMemeSwapList(walletGuid: string): Promise<any> {
        try {
            console.log("[MemeSwap API] Fetching meme swap list for wallet GUID:", walletGuid);
            const token = useWallet.getState().jwt;

            if (!token) {
                console.error("[MemeSwap API] Authentication token is missing");
                throw new Error("Authentication token is missing");
            }

            if (!walletGuid) {
                console.error("[MemeSwap API] Wallet GUID is missing");
                throw new Error("Wallet GUID is missing");
            }

            const response = await fetch(`${apiUrl}/mempad/swap/list`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ walletGuid }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`[MemeSwap API] Error ${response.status}: ${errorText}`);
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            console.log("[MemeSwap API] Response data:", data);

            // Check if the API returned empty data but with a successful status code
            if (!data || (Array.isArray(data) && data.length === 0)) {
                console.log("[MemeSwap API] API returned empty data array");
                return [];
            }

            if (data.error) {
                console.error("[MemeSwap API] API returned error:", data.error);
                throw new Error(data.error);
            }

            return data;
        } catch (error) {
            console.error("[MemeSwap API] Error fetching meme swap list:", error);
            throw error;
        }
    },
};
